import './styles/Calendar.scss';

import { useLazyQuery } from '@apollo/client';
import { Dialog, Paper, useMediaQuery, useTheme, Grid } from '@mui/material';

import clsx from 'clsx';
import { endOfMonth, endOfWeek, format, getDay, parse, startOfMonth, startOfWeek } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import { useState, FunctionComponent, useEffect } from 'react';
import { Calendar as BigCalendar, dateFnsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import QUERY from 'gql/queries/getAppointments.gql';

import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import useLocalStorage from 'utils/hooks/useLocalStorage';
import usePageHeading from 'utils/hooks/usePageHeading';

import { CalendarEntryDetail } from './components/CalendarEntryDetail';
import { SettingsDrawer } from './components/SettingsDrawer';
import { Toolbar } from './components/Toolbar';
import { SelectedEventsModel } from './types';

const locales = {
  'en-US': enUS,
  'de-DE': de
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales
});

export const Calendar: FunctionComponent = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const day = new URLSearchParams(useLocation().search).get('day');
  const [open, setOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<any | null>(null);
  const smallAndDown = useMediaQuery(theme.breakpoints.down('sm'));
  const [showArticles, setShowArticles] = useLocalStorage<SelectedEventsModel>(
    // remember to change the local storage key everytime the datamodel changes otherwise users will go on loading
    // the old state model from localstorage
    'calendar_selected_events-v8',
    {
      study: true,
      event: true,
      general: true,
      holiday: true,
      legal: true
    }
  );
  let startDate = startOfWeek(startOfMonth(new Date()));
  let endDate = endOfWeek(endOfMonth(new Date()));

  if (day) {
    const dayDate = new Date(day);
    startDate = startOfWeek(startOfMonth(dayDate));
    endDate = endOfWeek(endOfMonth(dayDate));
  }
  usePageHeading({
    heading: t(translations.pages.calendar.title()),
    mobilePadding: false
  });
  const dispatch = useDispatch();
  const [loadAppointments, { data }] = useLazyQuery(QUERY, {
    variables: {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    },
    fetchPolicy: 'cache-and-network',
    onError: error => {
      dispatch(
        AlertActions.error(`${t(translations.pages.calendar.openFailed())}: ${error.message}`)
      );
    }
  });

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  const handleToggleDrawer = (open: boolean) => {
    setDrawerOpen(open);
  };
  const handleClickOpen = event => {
    setSelectedEvent(event);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const onRangeChange = (date: Date, view: string) => {
    loadAppointments({
      variables: {
        startDate: startOfWeek(startOfMonth(date)).toISOString(),
        endDate: endOfWeek(endOfMonth(date)).toISOString()
      }
    });
  };

  const filterAppointments = (appointments: any[] | null | undefined) => {
    if (appointments === null || appointments === undefined || appointments?.length === 0) {
      return [];
    }
    return showArticles !== undefined
      ? appointments.filter(event => showArticles[event.article.type])
      : appointments;
  };

  return (
    <>
      <SettingsDrawer
        open={drawerOpen}
        toggleDrawer={handleToggleDrawer}
        showArticles={showArticles}
        setShowArticles={setShowArticles}
      />
      <Dialog
        aria-labelledby="calendar-dialog"
        open={open}
        onClose={handleClose}
        fullScreen={smallAndDown}
        PaperProps={{
          style: {
            maxWidth: '1050px',
            alignItems: 'center'
          }
        }}
      >
        {selectedEvent && (
          <CalendarEntryDetail id={selectedEvent.article.id} setDialogOpen={setOpen} />
        )}
      </Dialog>
      <Grid
        component={Paper}
        container
        direction="column"
        className={clsx('calendar__body', {
          'calendar__body--drawerExpanded': drawerOpen
        })}
      >
        <Grid item xs={12}>
          <BigCalendar
            messages={{
              date: t(translations.pages.calendar.calendar_props.date()),
              time: t(translations.pages.calendar.calendar_props.time()),
              event: t(translations.pages.calendar.calendar_props.event()),
              allDay: t(translations.pages.calendar.calendar_props.allDay()),
              week: t(translations.pages.calendar.calendar_props.week()),
              day: t(translations.pages.calendar.calendar_props.day()),
              month: t(translations.pages.calendar.calendar_props.month()),
              previous: t(translations.pages.calendar.calendar_props.previous()),
              next: t(translations.pages.calendar.calendar_props.next()),
              yesterday: t(translations.pages.calendar.calendar_props.yesterday()),
              tomorrow: t(translations.pages.calendar.calendar_props.tomorrow()),
              today: t(translations.pages.calendar.calendar_props.today()),
              agenda: t(translations.pages.calendar.calendar_props.agenda()),
              noEventsInRange: t(translations.pages.calendar.calendar_props.noEventsInRange()),
              showMore: function (e) {
                return '+' + e + ' ' + t(translations.pages.calendar.calendar_props.showMore());
              }
            }}
            onNavigate={(date, view) => onRangeChange(date, view)}
            className="calendar__body__big-calendar"
            localizer={localizer}
            defaultView={day ? 'day' : 'month'}
            popup={true}
            events={filterAppointments(data?.appointments)}
            titleAccessor={event => event.article.headline}
            tooltipAccessor={event => event.article.headline}
            startAccessor={event => new Date(event.startDate)}
            endAccessor={event => new Date(event.endDate)}
            views={{ month: true, week: true, day: true }}
            defaultDate={day ? new Date(day) : new Date()}
            style={{ height: smallAndDown ? '92vh' : '85vh' }}
            dayLayoutAlgorithm="no-overlap"
            components={{
              toolbar: props => <Toolbar openDrawer={setDrawerOpen} {...props} />
            }}
            onSelectEvent={event => handleClickOpen(event)}
            culture={i18n.language}
            eventPropGetter={(event: any) => {
              if (event.article.type === 'general') {
                return {
                  style: {
                    backgroundColor: '#714D09',
                    opacity: 0.8
                  }
                };
              }
              if (event.article.type === 'study') {
                return {
                  style: {
                    backgroundColor: '#D1B100',
                    opacity: 0.7
                  }
                };
              }
              if (event.article.type === 'event') {
                return {
                  style: {
                    backgroundColor: '#3D42EC',
                    opacity: 0.7
                  }
                };
              }
              if (event.article.type === 'legal') {
                return {
                  style: {
                    backgroundColor: '#dc0000',
                    opacity: 0.7
                  }
                };
              }
              if (event.article.type === 'holiday') {
                return {
                  style: {
                    backgroundColor: '#8DA028',
                    opacity: 0.7
                  }
                };
              }
              return {
                style: {
                  backgroundColor: '#000',
                  opacity: 0.7
                }
              };
            }}
          />
        </Grid>
      </Grid>
    </>
  );
};
